import { WorldImage } from '@components/Images/WorldImage';
import { MapMarker } from '@components/Sections/CoverageSection/MapMarker';
import Title from 'antd/lib/typography/Title';
import React, { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { CountryCodeEnum } from '@org-rixsus-it/rxs-shared';
import { Button, Image } from 'antd';
import { HashLink } from 'react-router-hash-link';
import { motion, useInView } from 'framer-motion';

export interface PaymentMethod {
  icon: string;
  name: string;
}

export interface PaymentMethodsByCountry {
  [countryCode: string]: PaymentMethod[];
}

export const CoverageSection = () => {
  const { t } = useTranslation();

  const ref = useRef(null);

  const isInView = useInView(ref, { margin: '-100px', once: false });

  const paymentMethodsByCountry: PaymentMethodsByCountry = {
    AR: [
      { icon: '/images/icons/banktransfer.svg', name: 'banktransfer' },
      { icon: '/images/icons/qr.svg', name: 'qr' },
    ],
    BR: [{ icon: '/images/icons/pix.svg', name: 'pix' }],
    CO: [
      { icon: '/images/icons/pse.png', name: 'pse' },
      { icon: '/images/icons/banktransfer.svg', name: 'banktransfer' },
    ],
    MX: [
      { icon: '/images/icons/codi.png', name: 'codi' },
      { icon: '/images/icons/spei.png', name: 'spei' },
    ],
  };

  return (
    <section
      id="coverage"
      ref={ref}
      className="relative flex flex-col items-center text-center bg-[#EDF6FB] rounded-[30px] max-w-[1204px] mx-auto w-full py-5 px-6 scroll-mt-[200px] sm:scroll-mt-[330px] lg:scroll-mt-[120px]"
    >
      <motion.div
        initial={{ opacity: 0, y: 40 }}
        animate={isInView ? { opacity: 1, y: 0 } : {}}
        transition={{ duration: 0.6 }}
      >
        <h2 className="font-bold text-[14px] leading-[17px] uppercase text-[#005FCC] text-center">
          {t('coverage.title')}
        </h2>
        <Title
          level={2}
          className="mx-auto w-full max-w-[622px] font-bold text-[32px] leading-[40px] md:text-[40px] md:leading-[48px] text-center text-black font-sans"
        >
          {t('coverage.subTitlePre')} <br /> {t('coverage.subTitlePost')}
        </Title>
      </motion.div>

      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={isInView ? { opacity: 1, scale: 1 } : {}}
        transition={{ delay: 0.2, duration: 0.8 }}
        className="relative flex justify-center items-center mt-[30px]"
      >
        <div
          className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2
                    rounded-full border-4 border-dashed border-[#1D3D59]
                    w-[110%] aspect-square
                    z-0"
        />

        <WorldImage alt="LATAM Map" />
        <div className="z-20">
          <MapMarker
            country="Mexico"
            flag="/images/flags/mexico.svg"
            paymentMethods={paymentMethodsByCountry[CountryCodeEnum.MX]}
            style={{ left: '10%', top: '29%' }}
          />
        </div>
        <div className="z-10">
          <MapMarker
            country="Colombia"
            flag="/images/flags/colombia.svg"
            paymentMethods={paymentMethodsByCountry[CountryCodeEnum.CO]}
            style={{ left: '30%', top: '40%' }}
          />
        </div>
        <div className="z-20">
          <MapMarker
            country="Brazil"
            flag="/images/flags/brazil.svg"
            paymentMethods={paymentMethodsByCountry[CountryCodeEnum.BR]}
            style={{ left: '65%', top: '50%' }}
          />
        </div>
        <div className="z-20">
          <MapMarker
            country="Argentina"
            defaultActiveKey
            flag="/images/flags/argentina.svg"
            paymentMethods={paymentMethodsByCountry[CountryCodeEnum.ARGENTINA]}
            style={{ left: '60%', top: '80%' }}
          />
        </div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={isInView ? { opacity: 1, y: 0 } : {}}
        transition={{ delay: 0.4, duration: 0.6 }}
        className="pt-14"
      >
        <HashLink to="/coverage-detail#coverageDetail">
          <Button
            type="primary"
            className="!flex !justify-center !items-center
                        !px-8 !py-3 !gap-1
                        !w-[199px] !h-[56px]
                        !bg-[#005FCC] !text-white
                        !rounded-full
                        !text-base !font-medium
                        hover:!bg-[#004bb0] transition-colors"
          >
            {t('common.more')}
            <Image
              preview={false}
              src="/images/icons/arrow-right.svg"
              alt="arrow"
            />
          </Button>
        </HashLink>
      </motion.div>
    </section>
  );
};
